import { KeyManagementService } from '@/services/KeyManagementService';
import { AsymmetricTreezorEncryption } from '@/services/encryption/AsymmetricTreezorEncryption';
import { SymmetricTreezorEncryption } from '@/services/encryption/SymmetricTreezorEncryption';
import { getSupportId } from '@/features/dashboard-users/helpers/getSupportId';
import { addUserKeys, setGlobalSupportKeys } from '@/store/actions/keyManagment.actions';
import { routeStore, store } from '@/store/configuration';
import { IEndUserKeys, IPairKey, ISupportUserKeys } from '@/store/models/encryption.model';

export class EncryptionService {
  private static instance: EncryptionService;
  asymmetric: AsymmetricTreezorEncryption = AsymmetricTreezorEncryption.getInstance();
  symmetric: SymmetricTreezorEncryption = SymmetricTreezorEncryption.getInstance();

  private constructor() {}

  static async getInstance(): Promise<EncryptionService> {
    if (!this.instance) {
      this.instance = new EncryptionService();
      await this.instance.init();
    }

    return this.instance;
  }

  async init() {
    await this.asymmetric.init();
    await this.symmetric.init();
  }

  async createEndUserKeys(passphrase: string): Promise<IEndUserKeys> {
    try {
      let globalSupportPublicKey = store.getState().encryption.globalSupportPairKey?.publicKey;
      if (!globalSupportPublicKey) {
        const globalSupportPublicKeyResponse = await KeyManagementService.fetchGlobalSupportPublicKey();
        globalSupportPublicKey = globalSupportPublicKeyResponse.globalSupportPublicKey;
      }

      const endUserPairKey = await this.asymmetric.generateKeyPair();

      return {
        publicKey: endUserPairKey.publicKey,
        encryptedPrivateKey: {
          withGlobalSupportPublicKey: await this.asymmetric.encryptKey(
            endUserPairKey.privateKey,
            globalSupportPublicKey,
          ),
          withUserSecret: await this.symmetric.encryptKey(endUserPairKey.privateKey, passphrase),
        },
      };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async decryptGlobalSupportPairKey(): Promise<IPairKey> {
    try {
      const supportUserId = getSupportId();
      const suppportUserPassphrase = routeStore.supportUserPassphrase;

      if (!supportUserId || !suppportUserPassphrase) return Promise.reject('Support user id or passhprase not found');

      /*
       * We will fetch keys of supporUser and endUser keys
       */
      const supportUserKeys = await KeyManagementService.fetchSupportUserKeys(supportUserId);
      const globalSupportPublicKeyResponse = await KeyManagementService.fetchGlobalSupportPublicKey();

      let supportUserPrivateKey, globalSupportPrivateKey;
      /*
       * Decrypt Support User Private Key With Passphrase
       */
      supportUserPrivateKey = await this.symmetric.decryptKey(
        supportUserKeys.encryptedPrivateKey.withUserSecret,
        suppportUserPassphrase,
      );

      supportUserPrivateKey = this.asymmetric.adaptPrivateKey(supportUserKeys.publicKey, supportUserPrivateKey);

      /*
       * Decrypt Global Support Private Key With Support User Private Key
       */
      globalSupportPrivateKey = await this.asymmetric.decryptKey(
        supportUserKeys.encryptedGlobalSupportPrivateKey.withUserPublicKey,
        supportUserKeys.publicKey,
        supportUserPrivateKey,
      );

      globalSupportPrivateKey = this.asymmetric.adaptPrivateKey(
        globalSupportPublicKeyResponse.globalSupportPublicKey,
        globalSupportPrivateKey,
      );

      /*
       * Set Global Support key pair
       */
      const globalSupportKeyPair = {
        publicKey: globalSupportPublicKeyResponse.globalSupportPublicKey,
        privateKey: globalSupportPrivateKey,
      };

      store.dispatch(setGlobalSupportKeys(globalSupportKeyPair));

      return globalSupportKeyPair;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async generateSupportUserKeys(passphrase: string): Promise<ISupportUserKeys> {
    const supportUserKeyPair = await this.asymmetric.generateKeyPair();

    const supportUserPrivateKeyWithPassphrase = await this.symmetric.encryptKey(
      supportUserKeyPair.privateKey,
      passphrase,
    );

    const globalSupportPairKey = await this.decryptGlobalSupportPairKey();

    const globalSupportPrivateKeyEncryptBySupportUserPublicKey = await this.asymmetric.encryptKey(
      globalSupportPairKey.privateKey,
      supportUserKeyPair.publicKey,
    );

    return {
      encryptedPrivateKey: {
        withUserSecret: supportUserPrivateKeyWithPassphrase,
      },
      encryptedGlobalSupportPrivateKey: {
        withUserPublicKey: globalSupportPrivateKeyEncryptBySupportUserPublicKey,
      },
      publicKey: supportUserKeyPair.publicKey,
    };
  }

  async decryptEndUserPrivateKey(endUserId: number): Promise<IPairKey> {
    try {
      let globalSupportPairKey = store.getState().encryption.globalSupportPairKey;

      if (!globalSupportPairKey) {
        globalSupportPairKey = await this.decryptGlobalSupportPairKey();
      }

      /*
       * Decrypt End User Private Key with Global Support Private Key
       */
      const endUserKeys = await KeyManagementService.fetchEndUserKeys(endUserId);
      if (!endUserKeys) return Promise.reject('User Keys not found');

      if (!globalSupportPairKey) return Promise.reject('Global Support Keys not found');

      let endUserPrivateKey = await this.asymmetric.decryptKey(
        endUserKeys.encryptedPrivateKey.withGlobalSupportPublicKey,
        globalSupportPairKey.publicKey,
        globalSupportPairKey.privateKey,
      );

      endUserPrivateKey = this.asymmetric.adaptPrivateKey(endUserKeys.publicKey, endUserPrivateKey);

      /*
       * Set End User key pair
       */
      const userPairKey = {
        publicKey: endUserKeys.publicKey,
        privateKey: endUserPrivateKey,
      };

      store.dispatch(addUserKeys(endUserId, userPairKey));

      return userPairKey;
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
